import { refreshAdmin } from './admin-auth-api';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';

export type AvomanReviewStatus = 'pending' | 'accepted' | 'rejected';
export type AvomanClassification =
  | 'style'
  | 'boundary'
  | 'knowledge'
  | 'mixed'
  | 'eval_candidate'
  | 'general';

export interface AvomanCalibrationTurn {
  id: string;
  calibrationSessionId: string;
  userMessage: string;
  assistantAnswer: string;
  tags: string[];
  classification: AvomanClassification;
  reviewStatus: AvomanReviewStatus;
  createdAt: string;
}

export interface ListAvomanCalibrationTurnsInput {
  reviewStatus: AvomanReviewStatus;
  limit: number;
}

export interface ReviewAvomanCalibrationTurnInput {
  reviewStatus: AvomanReviewStatus;
  classification?: AvomanClassification;
}

export interface ExportAvomanCalibrationBatchInput {
  reviewStatus: AvomanReviewStatus;
  limit: number;
  includeRejected: boolean;
}

export type AvomanCalibrationBatch = Record<string, unknown> & {
  batch_id?: string;
  count?: number;
  items?: unknown[];
};

interface ApiAvomanCalibrationTurn {
  id: string;
  calibration_session_id: string;
  user_message: string;
  assistant_answer: string;
  tags: string[];
  classification: AvomanClassification;
  review_status: AvomanReviewStatus;
  created_at: string;
}

let adminSessionRefreshPromise: Promise<string | null> | null = null;

async function readErrorMessage(response: Response): Promise<string> {
  const fallback = response.statusText || 'Request failed.';
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as { detail?: unknown };
      if (typeof payload.detail === 'string' && payload.detail.trim()) {
        return payload.detail;
      }
      if (payload.detail != null) {
        return JSON.stringify(payload.detail);
      }
      return JSON.stringify(payload);
    } catch {
      return fallback;
    }
  }

  const text = await response.text().catch(() => fallback);
  return text || fallback;
}

async function refreshAdminAccessToken(): Promise<string | null> {
  if (adminSessionRefreshPromise) {
    return adminSessionRefreshPromise;
  }

  adminSessionRefreshPromise = refreshAdmin()
    .then((session) => {
      useAdminAuthStore.getState().setSession(session.accessToken, session.user);
      return session.accessToken;
    })
    .catch(() => {
      useAdminAuthStore.getState().clearSession();
      return null;
    })
    .finally(() => {
      adminSessionRefreshPromise = null;
    });

  return adminSessionRefreshPromise;
}

function sendAdminRequest(
  token: string,
  input: RequestInfo | URL,
  init: RequestInit,
): Promise<Response> {
  return fetch(input, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.headers ?? {}),
    },
  });
}

async function sendAdminRequestWithRefresh(
  accessToken: string,
  input: RequestInfo | URL,
  init: RequestInit,
): Promise<Response> {
  let response = await sendAdminRequest(accessToken, input, init);

  if (response.status === 401) {
    const refreshedAccessToken = await refreshAdminAccessToken();
    if (refreshedAccessToken) {
      response = await sendAdminRequest(refreshedAccessToken, input, init);
    }
  }

  return response;
}

async function requestAvomanCalibration<T>(
  accessToken: string,
  input: RequestInfo | URL,
  init: RequestInit,
  actionLabel: string,
): Promise<T> {
  const response = await sendAdminRequestWithRefresh(accessToken, input, init);

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(`${actionLabel} failed (${response.status}): ${message}`);
  }

  return response.json() as Promise<T>;
}

function normalizeTurn(payload: ApiAvomanCalibrationTurn): AvomanCalibrationTurn {
  return {
    id: payload.id,
    calibrationSessionId: payload.calibration_session_id,
    userMessage: payload.user_message,
    assistantAnswer: payload.assistant_answer,
    tags: payload.tags,
    classification: payload.classification,
    reviewStatus: payload.review_status,
    createdAt: payload.created_at,
  };
}

export async function listAvomanCalibrationTurns(
  accessToken: string,
  input: ListAvomanCalibrationTurnsInput,
): Promise<AvomanCalibrationTurn[]> {
  const params = new URLSearchParams({
    review_status: input.reviewStatus,
    limit: String(input.limit),
  });
  const payload = await requestAvomanCalibration<ApiAvomanCalibrationTurn[]>(
    accessToken,
    `/api/v1/admin/avoman-calibration/turns?${params.toString()}`,
    { method: 'GET' },
    'List Avoman calibration turns',
  );
  return payload.map(normalizeTurn);
}

export async function reviewAvomanCalibrationTurn(
  accessToken: string,
  turnId: string,
  input: ReviewAvomanCalibrationTurnInput,
): Promise<AvomanCalibrationTurn> {
  const payload = await requestAvomanCalibration<ApiAvomanCalibrationTurn>(
    accessToken,
    `/api/v1/admin/avoman-calibration/turns/${turnId}/review`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        review_status: input.reviewStatus,
        classification: input.classification,
      }),
    },
    'Review Avoman calibration turn',
  );
  return normalizeTurn(payload);
}

export async function exportAvomanCalibrationBatch(
  accessToken: string,
  input: ExportAvomanCalibrationBatchInput,
): Promise<AvomanCalibrationBatch> {
  return requestAvomanCalibration<AvomanCalibrationBatch>(
    accessToken,
    '/api/v1/admin/avoman-calibration/export',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        review_status: input.reviewStatus,
        limit: input.limit,
        include_rejected: input.includeRejected,
      }),
    },
    'Export Avoman calibration batch',
  );
}
